import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getUser } from "../../../utils/storage";
import { axiosInstance } from "../../../utils/axios";
import { CheckIcon, XMarkIcon } from "@heroicons/react/20/solid";
import AdminSidebar from "../../../components/AdminSidebar";
import { toast } from "react-toastify";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import NoData from "../../../components/NoData";
import LoadingComponent from "../../../components/LoadingComponent";
import PaginationV2 from "../../../components/PaginationV2";

export default function ApproveUsers() {
  const [searchQuery, setSearchQuery] = useState("");

  const user = getUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const page = params.get("page") || 1;

  const fetchUsers = async ({ search = "" }) => {
    if (search) {
      const response = await axiosInstance.get(
        window.REACT_APP_API_ENDPOINT + "/users/search/test?q=" + search
      );
      return response.data;
    }
    const response = await axiosInstance.get(
      window.REACT_APP_API_ENDPOINT + "/users?status=Pending&page=" + page
    );
    return response.data;
  };

  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["pendingUsers", searchQuery],
    queryFn: () => fetchUsers({ search: searchQuery }),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }) => {
      const response = await axiosInstance.patch(
        `${window.REACT_APP_API_ENDPOINT}/users/${id}/status?status=${status}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingUsers"] });
      toast.success("User status updated successfully!");
    },
    onError: (error) => {
      toast.error("Failed to update user status. Please try again.");
      console.error("Update status error:", error);
    },
  });

  const handleStatusChange = (id, status) => {
    updateStatusMutation.mutate({ id, status });
  };

  if (isLoading) {
    return <p>Loading...</p>;
  }

  const users = usersData?.data || [];
  const totalPages = usersData?.last_page || 1;

  return (
    <AdminSidebar>
      <div className="px-4 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="sm:flex flex-col sm:items-start">
          <div className="w-full flex justify-between py-4 px-4 bg-white rounded-lg">
            <h3 className="font-bold text-2xl">👋 Hello {user.name}</h3>
          </div>
        </div>
        <div className="mt-8 flow-root bg-white rounded-lg">
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block lg:min-w-full max-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="w-full flex flex-col lg:flex-row justify-between items-center py-3 px-4 bg-white rounded-lg">
                <h4 className="font-semibold text-2xl mb-2 lg:mb-0">
                  Pending Users
                </h4>
                <input
                  type="text"
                  name="search"
                  id="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="mb-2 lg:mb-0 block w-70 rounded-md border-0 py-1.5 pr-14 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <div className="flex items-center">
                  <a
                    href="/admin/users"
                    className="bg-bluePrimary text-white rounded-md px-4 py-3 font-bold mr-4"
                  >
                    Users
                  </a>
                  <a
                    href="/admin/users/form"
                    className="border border-bluePrimary text-bluePrimary rounded-md px-4 py-3 font-bold"
                  >
                    Register User +
                  </a>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-black">
                    <tr>
                      <th
                        scope="col"
                        className="py-3.5 pl-8 pr-3 text-left text-sm font-semibold text-white"
                      >
                        Full Name
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-white"
                      >
                        Username
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-white"
                      >
                        Status
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-3.5 text-left text-sm font-semibold text-white"
                      >
                        Role
                      </th>
                      <th
                        scope="col"
                        className="relative py-3.5 pl-3 pr-4 sm:pr-0"
                      >
                        <span className="sr-only">Edit</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {users?.data?.length == 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center">
                          <NoData message="No users found" />
                        </td>
                      </tr>
                    ) : (
                      users?.data?.map((user) => (
                        <tr key={user.id}>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center pl-4">
                              <div className="">
                                <div className="font-medium text-gray-900">
                                  {user.name} {user.last_name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <div className="mt-1 text-black">
                              {user.username}
                            </div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                              {user.status}
                            </span>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm text-black">
                            <div className="mt-1 text-black">{user.role}</div>
                          </td>
                          <td className="whitespace-nowrap p-3 text-sm sm:pl-0">
                            <div className="flex items-center space-x-2 pl-4">
                              <button
                                onClick={() =>
                                  handleStatusChange(user.id, "Approved")
                                }
                                className="p-2 rounded-full bg-green-100 hover:bg-green-200 transition duration-200"
                                aria-label="Approve"
                                disabled={updateStatusMutation.isPending}
                              >
                                <CheckIcon
                                  className="h-5 w-5 text-green-600"
                                  aria-hidden="true"
                                />
                              </button>
                              <button
                                onClick={() =>
                                  handleStatusChange(user.id, "Rejected")
                                }
                                className="p-2 rounded-full bg-red-100 hover:bg-red-200 transition duration-200"
                                aria-label="Reject"
                                disabled={updateStatusMutation.isPending}
                              >
                                <XMarkIcon
                                  className="h-5 w-5 text-red-600"
                                  aria-hidden="true"
                                />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}

              <PaginationV2
                last_page={usersData?.last_page}
                current_page={usersData?.current_page}
                next_page_url={usersData?.next_page_url}
                prev_page_url={usersData?.prev_page_url}
              />
            </div>
          </div>
        </div>
      </div>
    </AdminSidebar>
  );
}
